import React from 'react';
import { OptionalSection, Button, ToastContext, SelectLabel } from '@weezevent/nacre';
import LoadingCard from '../../../../../components/loadingCard';
import StatusSelect from './StatusSelect';
import LanguageSelect from './LanguageSelect';
import DateInput from './DateInput';
import CommentInput from './CommentInput';
import RatesSelect from './RatesSelect';
import Unavailable from '../Unavailable';
import { useTranslation } from 'react-i18next';
import ValidationModal from '../ValidationModal';
import css from '../../styles.module.css';
import { Booking } from '../../../../../models/bookingModel';
import Waiting from '../Waiting';
import useAppContext from '../../../../../context/useAppContext';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import { ORIGIN_STOCK_PRINT } from '../../../../../models/order';

export default function OrderStep({
    form,
    rates,
    ratesTree,
    onChangeForm,
    onChangeFormObject,
    onChangeSelectedRates,
    onChangeSteps,
    loadBuyerFields,
    step,
    event,
    quotas,
    inventories,
    bookingSelectionAuto,
    isSubmitting,
    handleSubmit = () => {},
    loadingRates,
    onConstructAttendees = () => {},
    unbookBooking = () => {},
    onChangeAllForm = () => {},
    getQuotaForOneRate = () => {},
    computeQuota = () => {}
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    let [{ organization }] = useAppContext();
    const [moreOptionsOpen, setMoreOptionOpen] = React.useState(form.comment !== null || form.orderDateModified);

    const sessionQuotasExceeded = React.useMemo(() => {
        if (form.use_sessions) {
            if (quotas[event.pk][form.sessions[0]['weez_date_id']]['available'] === Infinity) {
                return false;
            }
            return quotas[event.pk][form.sessions[0]['weez_date_id']]['available'] - quotas[event.pk][form.sessions[0]['weez_date_id']]['taken'] < 0;
        }
    }, [form, quotas, event]);

    const handleContinue = React.useCallback(async () => {
        if (form.use_inventories && !bookingSelectionAuto) {
            // Prevent incrementing attendees when returning to order step after manual selection
            track('ACTION > VALIDATE ORDER INFORMATION', { 'Page Title': 'ADD ATTENDEES' });
            return onChangeSteps(step + 1);
        }

        let attendeesToPush = form.attendees;
        let rates_to_create = form.selected_rates
            .map(s_rate => {
                // Get attendees already created for each rate
                let attendees_already = attendeesToPush.filter(
                    attendee => attendee.rate_id === s_rate.rate.pk && attendee.sessionId === s_rate.sessionId && s_rate.inventory?.pk === attendee.booking_uuid?.inventory
                );
                if (attendees_already.length === s_rate.quantity) {
                    return null;
                }

                if (attendees_already.length > s_rate.quantity) {
                    toast.error(t('partners.invitation.download_ticket_error'));
                    return null;
                }

                return { ...s_rate, quantity: s_rate.quantity - attendees_already.length };
            })
            .filter(s_rate => s_rate);

        if (form.inventories.length) {
            let inventories = [];
            // Get each inventories
            form.selected_rates.forEach(s_rate => {
                if (!inventories.includes(s_rate.inventory)) {
                    inventories.push(s_rate.inventory);
                }
            });

            let bookingPromises = [];

            inventories.forEach(inventory => {
                // Get categories
                let categories = [];
                let selected_rates_filtered = rates_to_create.filter(s_rate => s_rate.inventory === inventory);
                let rates_obj = selected_rates_filtered.map(s_rate => s_rate.rate);
                rates_obj.forEach(rate => {
                    if (rate.rate_template) {
                        rate = rate.rate_template;
                    }
                    if (rate.category && !categories.includes(rate.category)) {
                        categories.push(rate.category);
                    }
                });

                categories.forEach(cat => {
                    let booking = new Booking();
                    let seats = [];

                    selected_rates_filtered
                        .filter(s_rate => (s_rate.rate.category || s_rate.rate.rate_template.category) === cat)
                        .forEach(s_rate => {
                            for (let i = 0; i < s_rate.quantity; i++) {
                                seats.push([s_rate.rate.pk]);
                            }
                        });

                    booking.seats = seats;
                    booking.category = cat;

                    bookingPromises.push(
                        booking
                            .save({
                                organizationId: organization.pk,
                                chartId: event.chart,
                                inventoryId: inventory.pk,
                                zoneId: 'root',
                                query: {
                                    manual: false
                                }
                            })
                            .then(({ response }) => {
                                return response.seats;
                            })
                            .catch(e => {
                                onChangeSteps(step);
                                toast.error(t(`partners.invitation.errors.${e.response?.detail || 'unknownError'}`));
                            })
                    );
                });
            });

            Promise.all(bookingPromises)
                .then(promises => {
                    onConstructAttendees(promises.flat());
                })
                .catch(e => {
                    toast.error(t(`partners.invitation.errors.${e.response.detail ? e.response.detail : 'unknownError'}`));
                });
        } else {
            onConstructAttendees();
        }
        onChangeSteps(step + 1);
        track('ACTION > VALIDATE ORDER INFORMATION', { 'Page Title': 'ADD ATTENDEES' });
    }, [onConstructAttendees, Booking, step, onChangeSteps, loadBuyerFields, form, event, organization, bookingSelectionAuto]);

    const filteredRates = React.useMemo(() => {
        const rates = Array.from(new Set(form.selected_rates));
        return rates;
    }, [form.selected_rates]);

    const onSetRates = React.useCallback(
        (selected_rates, options = {}) => {
            // Set rates by creating all objects for selected_rates or updating them.
            const { session = null, inventory = null } = options;
            let ratesToPush = null;

            if (inventory) {
                ratesToPush = form.selected_rates.length
                    ? filteredRates.filter(rate => rate.inventory.uuid !== inventory.uuid)
                    : filteredRates.filter(rate => rate.inventory.uuid === inventory.uuid);
            } else {
                ratesToPush = form.selected_rates.length ? [] : form.selected_rates;
            }

            selected_rates.forEach(rate => {
                let old_conf = form.selected_rates.find(r => r.id === rate.id && rate.sessionId === session.value && rate.inventory === inventory);

                let fees = rate.fees?.reduce((fee, obj) => {
                    return fee + parseFloat(obj.amount);
                }, 0);
                let properPrice = String(parseFloat(rate.resolved_price));

                ratesToPush.push({
                    ...old_conf,
                    rate: rate,
                    inventory: inventory,
                    rateId: rate?.id,
                    quantity: !old_conf ? 1 : old_conf.quantity,
                    price: !old_conf ? properPrice : old_conf.price,
                    fees
                });
            });
            onChangeSelectedRates(ratesToPush);
        },
        [onChangeSelectedRates, form, filteredRates]
    );

    const onUnselectRates = React.useCallback(
        ratesToUnselect => {
            // Unselect rates and unbook if needed
            // 1 - Check attendees with theses rates already exist
            let attendees = [];
            ratesToUnselect.forEach(s_rate => {
                let attendees_with_rate = form.attendees.filter(attendee => attendee.rate_id === s_rate.rate.pk);
                if (attendees_with_rate.length) {
                    attendees_with_rate.forEach(att => {
                        attendees.push(att);
                    });
                }
            });
            // 2 - Check if attendees has bookings -> unbook
            let attendeesToUnbook = [];
            attendees.forEach(attendee => {
                if (attendee.booking_uuid) {
                    attendeesToUnbook.push(attendee);
                }
            });
            if (attendeesToUnbook.length) {
                unbookBooking(attendeesToUnbook);
            }

            // 3 - Delete rates in selected_rates
            let selectedAttendees = form.attendees.filter(att => !attendees.includes(att));
            let selectedRates = form.selected_rates.filter(s_rate => !ratesToUnselect.includes(s_rate));

            onChangeAllForm({ ...form, selected_rates: selectedRates, attendees: selectedAttendees });
            computeQuota();
        },
        [form, onChangeAllForm, unbookBooking, computeQuota]
    );

    const onChangeSelectedRate = React.useCallback(
        (selected_rate, field, value) => {
            // This callback is used to change property quantity and price in selected_rates.
            let copySelectedRates = form.selected_rates;
            let selected_rate_index = copySelectedRates.findIndex(s_rate => s_rate.rate.id === selected_rate.rate.id);
            copySelectedRates[selected_rate_index][field] = value;
            onChangeSelectedRates(copySelectedRates);
        },
        [form, onChangeSelectedRates]
    );

    const ratesAndFees = React.useMemo(() => {
        return form.selected_rates.every(rate => (rate.fees > 0 && rate.price > 0 ? rate.price >= rate.fees : true));
    }, [form]);

    const ratesQuotasExceeded = React.useMemo(() => {
        /**
         * Return a boolean (over) to know if one of the selected rate has exceed quotas.
         */

        let over = false;

        // Retrieve the session Id. Maybe one day, we will have many sessions and this research will be inside of the foreach
        let session_id = form.sessions.length ? form.sessions[0]['weez_date_id'] : null;
        form.selected_rates.forEach(rate => {
            // get all remaining quota on each level.
            const rate_quota = getQuotaForOneRate({ rateId: rate.rate.id, groupId: rate.rate.group, sessionId: session_id });
            const quota = Math.min(...Object.values(rate_quota).filter(value => value !== null && value !== Infinity));
            if (quota < 0) {
                // if one of them is under 0, quota are exceeded
                over = true;
            }
        });
        return over;
    }, [form, quotas, getQuotaForOneRate]);

    const disableNextButton = React.useMemo(() => {
        if (!form.selected_rates.length || ratesQuotasExceeded || !ratesAndFees) {
            return true;
        }

        if (form.use_inventories) {
            let inventories_ids = form.selected_rates.map(s_rate => s_rate?.inventory?.pk);
            let isEvery = form.inventories.every(inventory => inventories_ids.includes(inventory.pk));
            if (!isEvery) {
                return true;
            }
        }

        if (form.use_sessions) {
            return sessionQuotasExceeded;
        }

        return false;
    }, [form, form.selected_rates, ratesAndFees, sessionQuotasExceeded, ratesQuotasExceeded]);

    const handleModal = React.useCallback(() => {
        onChangeForm('validationModal', true);
        track('ACTION > ADD NON-NOMINATIVE ATTENDEES', { 'Page Title': 'ADD ATTENDEES', Steps: 'Order' });
    }, [onChangeForm]);

    if (loadingRates || !rates || loadBuyerFields) {
        return <LoadingCard loading />;
    }

    if (rates.length === 0) {
        return <Unavailable />;
    }

    return (
        <Waiting for={rates.length}>
            <RatesSelect
                rates={rates}
                form={form}
                onOpen={() => track('ACTION > SELECT TICKET TYPES', { 'Page Title': 'ADD ATTENDEES' })}
                setRates={onSetRates}
                onChangeForm={onChangeForm}
                inventories={inventories}
                bookingSelectionAuto={bookingSelectionAuto}
                onChangeSelectedRate={onChangeSelectedRate}
                onUnselectRates={onUnselectRates}
                ratesTree={ratesTree}
                loadingRates={loadingRates}
                quotas={quotas}
            />
            <SelectLabel
                useNew
                label={t('attendees.addAttendees.orderOrigin')}
                placeholder={t('ticket.printer.choose_support_type')}
                value={form.payment_type}
                options={[
                    {
                        key: 6,
                        value: 6,
                        text: t('attendees.addAttendees.orderOriginManual')
                    },
                    {
                        key: 8,
                        value: 8,
                        text: t('attendees.addAttendees.orderOriginPrintStock')
                    }
                ]}
                onChange={value =>
                    onChangeFormObject(
                        {
                            payment_type: value,
                            downloadPDF: value === ORIGIN_STOCK_PRINT ? true : false
                        },
                        true
                    )
                }
            />
            <div className={css['select-labels-group']}>
                <StatusSelect
                    form={form}
                    setStatus={status => {
                        onChangeFormObject(status);
                        track('ACTION > SELECT PAYMENT STATUS', { 'Page Title': 'ADD ATTENDEES', 'Payment Status': status.status === 'valid' ? 'Paid' : 'Booked' });
                    }}
                />
                <LanguageSelect
                    onOpen={() => track('ACTION > SELECT ORDER LANGUAGE', { 'Page Title': 'ADD ATTENDEES' })}
                    label={t('attendees.addAttendees.orderLanguage')}
                    language={form.languageLabel}
                    setLanguage={value => onChangeForm('languageLabel', value)}
                />
            </div>
            <OptionalSection
                open={moreOptionsOpen}
                onToggle={toggle => {
                    track('ACTION > DISPLAY ADVANCED OPTIONS', { 'Page Title': 'ADD ATTENDEES' });
                    setMoreOptionOpen(toggle);
                }}
                label={t('attendees.addAttendees.optionalFields')}
            >
                <div className={css['attendees-optional-section']}>
                    <DateInput
                        onClick={() => {
                            track('ACTION > ORDER DATE', { 'Page Title': 'ADD ATTENDEES' });
                        }}
                        date={form.orderDate}
                        onChange={onChangeForm}
                        form={form}
                    />
                    <CommentInput
                        onFocus={() => {
                            track('ACTION > COMMENTS', { 'Page Title': 'ADD ATTENDEES' });
                        }}
                        comment={form.comment}
                        onChange={onChangeForm}
                    />
                </div>
            </OptionalSection>
            <div className={css['select-labels-btns']}>
                <Button className={css['progress-buttons']} primary disabled={disableNextButton} label={t('ticket.common.action.continue')} onClick={handleContinue} />
                {/* we hide a button until we have attendee booking, submit needs booking id in payload otherwise it fails */}
                {!form.use_inventories && form.payment_type !== ORIGIN_STOCK_PRINT && (
                    <Button disabled={disableNextButton} className={css['tweak-label-btn']} label={t('attendees.addAttendees.addNonNominativeAttendees')} onClick={handleModal} />
                )}
            </div>
            {form.validationModal && (
                <ValidationModal form={form} setForm={onChangeForm} handleSubmit={handleSubmit} onConstructAttendees={onConstructAttendees} order isSubmitting={isSubmitting} />
            )}
        </Waiting>
    );
}
